import { useState, useEffect, useRef } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, Stack, MenuItem, Divider, Alert,
} from '@mui/material';
import { type PartnerRow } from '../../services/partnerUserService';
import { SERVICE_HUB_OPTIONS, getPartnerConfig } from '../../constants/partnerServices';
import { getUserTypeFormConfig, PARTNER_USER_TYPES } from '../../constants/userManagement';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneWithDialCodeField from '../ui/forms/PhoneWithDialCodeField';
import {
  validatePartnerContactForm,
  partnerValidationSummary,
  clearPartnerFieldErrors,
} from '../../utils/partnerFormValidation';
import { PERSON_NAME_MIN, PERSON_NAME_MAX } from '../../utils/personFieldValidation';
import { composeE164, localPhoneDigits, splitPhoneByDialCodes } from '../../utils/phoneDialCode';
import { getCachedCountryDialCodes } from '../../services/countryDialCodeService';

interface ZoneOption { _id: string; name: string }

interface PartnerEditDrawerProps {
  open: boolean;
  onClose: () => void;
  partner: PartnerRow | null;
  zones: ZoneOption[];
  onSave: (body: Record<string, unknown>) => void;
  loading?: boolean;
}

function splitPartnerPhone(raw?: string, storedCode?: string): { phone: string; phoneCountryCode: string } {
  const dialCodes = getCachedCountryDialCodes().map((o) => o.dialCode);
  if (raw?.startsWith('+')) {
    const split = splitPhoneByDialCodes(raw, dialCodes);
    return {
      phoneCountryCode: storedCode || split.dialCode || '+91',
      phone: split.localNumber || localPhoneDigits(raw),
    };
  }
  return {
    phoneCountryCode: storedCode || '+91',
    phone: localPhoneDigits(raw || ''),
  };
}

export default function PartnerEditDrawer({ open, onClose, partner, zones, onSave, loading }: PartnerEditDrawerProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCountryCode: '',
    zoneId: '',
    serviceHub: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const partnerEntityType = partner?.partnerEntityType || partner?.userType || '';
  const partnerConfig = getPartnerConfig(partnerEntityType);
  const requiresZone = PARTNER_USER_TYPES.has(partnerConfig?.userType || partnerEntityType)
    || getUserTypeFormConfig(partnerConfig?.userType || partnerEntityType).requiresZone;

  useEffect(() => {
    if (!partner) return;
    setErrors({});
    const meta = partner.metadata as { phoneCountryCode?: string } | undefined;
    const phoneSplit = splitPartnerPhone(partner.phone, meta?.phoneCountryCode);
    setForm({
      firstName: partner.firstName,
      lastName: partner.lastName || '',
      email: partner.email || '',
      phone: phoneSplit.phone,
      phoneCountryCode: phoneSplit.phoneCountryCode,
      zoneId: partner.zoneId?._id || '',
      serviceHub: partner.serviceHub || '',
    });
  }, [partner]);

  const scrollToFirstError = () => {
    requestAnimationFrame(() => {
      const first = formRef.current?.querySelector<HTMLElement>(
        '.Mui-error input, .Mui-error textarea, .Mui-error [role="combobox"]',
      );
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      first?.focus({ preventScroll: true });
    });
  };

  const patchErrors = (keys: string[]) => {
    setErrors((prev) => clearPartnerFieldErrors(prev, keys));
  };

  const validate = () => {
    const next = validatePartnerContactForm(form, { requiresZone, zones });
    setErrors(next);
    if (Object.keys(next).length) scrollToFirstError();
    return Object.keys(next).length === 0;
  };

  const validationLines = partnerValidationSummary(errors);
  const nameHelper = `Letters only · ${PERSON_NAME_MIN}–${PERSON_NAME_MAX} characters`;

  if (!open || !partner) return null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Edit Partner</Typography>
        {validationLines.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Please fix the following ({validationLines.length}):
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {validationLines.map((line) => (
                <Typography key={line} component="li" variant="body2">{line}</Typography>
              ))}
            </Box>
          </Alert>
        )}
        <Stack
          component="form"
          ref={formRef}
          spacing={2}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!validate()) return;
            const phoneE164 = form.phone.trim()
              ? composeE164(form.phoneCountryCode, form.phone)
              : undefined;
            onSave({
              firstName: form.firstName.trim(),
              lastName: form.lastName.trim() || undefined,
              email: form.email.trim() || undefined,
              phone: phoneE164,
              zoneId: form.zoneId || undefined,
              serviceHub: form.serviceHub || undefined,
              metadata: {
                phoneCountryCode: form.phoneCountryCode || undefined,
              },
            });
          }}
        >
          <PersonNameField
            label="First Name"
            required
            fullWidth
            size="small"
            value={form.firstName}
            error={!!errors.firstName}
            helperText={errors.firstName || nameHelper}
            inputProps={{ maxLength: PERSON_NAME_MAX }}
            onChange={(e) => {
              patchErrors(['firstName']);
              setForm({ ...form, firstName: e.target.value });
            }}
          />
          <PersonNameField
            label="Last Name"
            fullWidth
            size="small"
            value={form.lastName}
            error={!!errors.lastName}
            helperText={errors.lastName || nameHelper}
            inputProps={{ maxLength: PERSON_NAME_MAX }}
            onChange={(e) => {
              patchErrors(['lastName']);
              setForm({ ...form, lastName: e.target.value });
            }}
          />
          <TextField
            label="Email"
            type="email"
            required={!form.phone.trim()}
            fullWidth
            size="small"
            value={form.email}
            error={!!errors.email || !!errors.contact}
            helperText={
              errors.email
              || errors.contact
              || 'Required when phone is not provided'
            }
            onChange={(e) => {
              patchErrors(['email', 'contact']);
              setForm({ ...form, email: e.target.value });
            }}
          />
          <PhoneWithDialCodeField
            label="Phone"
            required={!form.email.trim()}
            phone={form.phone}
            countryCode={form.phoneCountryCode}
            preferredCountry="IN"
            error={!!errors.phone || !!errors.contact}
            helperText={errors.phone || errors.contact || 'Required when email is not provided'}
            onChange={({ phone, countryCode }) => {
              patchErrors(['phone', 'contact']);
              setForm({
                ...form,
                phone,
                phoneCountryCode: countryCode,
              });
            }}
          />
          <TextField
            select
            label="Zone"
            required={requiresZone}
            fullWidth
            size="small"
            value={form.zoneId}
            error={!!errors.zoneId}
            helperText={
              errors.zoneId
              || (requiresZone ? 'Required — assign the partner to a service zone' : '')
            }
            onChange={(e) => {
              patchErrors(['zoneId']);
              setForm({ ...form, zoneId: e.target.value });
            }}
          >
            <MenuItem value="" disabled={requiresZone}>
              {requiresZone ? 'Select a zone' : 'No zone'}
            </MenuItem>
            {zones.map((z) => <MenuItem key={z._id} value={z._id}>{z.name}</MenuItem>)}
          </TextField>
          <TextField select label="Service Hub" fullWidth size="small" value={form.serviceHub} onChange={(e) => setForm({ ...form, serviceHub: e.target.value })}>
            {SERVICE_HUB_OPTIONS.map((h) => <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>)}
          </TextField>
          <Divider sx={{ my: 0.5 }} />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button type="button" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
